/**
 * 고객사 브랜드 — 콘솔의 템플릿 미리보기가 화면과 같은 머리글·색·글꼴로 그리게 한다.
 *
 * 환경 설정을 한 번 읽어 앱 전체에 나눠 준다. 미리보기는 목록 썸네일·편집기·모니터링·화면 상세 곳곳에
 * 있어, 저마다 설정을 읽으면 같은 화면에서 서로 다른 머리글이 나올 수 있다. 환경 설정을 저장하면
 * 다시 읽는다(`reload`).
 *
 * 로고·글꼴은 서버가 준 **단명 서명 주소**로 읽는다. 오래 열어 둔 콘솔에서 주소가 만료되면 그 조각만
 * 빠진다 — 새로고침하면 돌아온다.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { brandFontFaceCss, DEFAULT_HEADER_PLACE, type HeaderPlaceSetting } from '@signageone/core';
import type { SignageOneClient, TenantSettings } from '../api/client.js';

export interface BrandLook {
  name: string;
  logoUrl: string | null;
  accent: string | null;
  font: { url: string; mime: string; filename: string } | null;
  /** 머리글의 장소 글을 정하는 방식(2026-11-03) — 미리보기가 화면과 같은 글을 적게(`TemplatePreview`). */
  place: HeaderPlaceSetting;
}

export const EMPTY_BRAND: BrandLook = { name: '', logoUrl: null, accent: null, font: null, place: DEFAULT_HEADER_PLACE };

export function brandFromSettings(settings: TenantSettings | null): BrandLook {
  if (!settings) return EMPTY_BRAND;
  const font = settings.brand.font;
  return {
    name: settings.brand.name,
    logoUrl: settings.brand.logo_preview_url,
    accent: settings.brand.accent,
    font: font ? { url: font.preview_url, mime: font.mime, filename: font.filename } : null,
    place: settings.brand.place ?? DEFAULT_HEADER_PLACE,
  };
}

interface BrandState {
  brand: BrandLook;
  /**
   * 올린 HTML 의 바깥 연결을 막았다(2026-11-03, 환경 설정의 `html.offline`) — 바깥 주소 알림(`HtmlExternalNote`)이 "빠진다" 로
   * 말한다. 브랜드와 같은 설정에서 오니 같은 자리에서 나눠 준다.
   */
  htmlOffline: boolean;
  /** 환경 설정을 저장한 뒤 부른다 — 미리보기가 새 모양으로 바뀐다. */
  reload: () => void;
}

const BrandContext = createContext<BrandState>({ brand: EMPTY_BRAND, htmlOffline: false, reload: () => undefined });

export function useBrand(): BrandState {
  return useContext(BrandContext);
}

const FONT_STYLE_ID = 'so-template-brand-font';

/**
 * 콘솔 문서에도 사내 글꼴을 건다 — 템플릿 편집기의 글꼴 고르기 견본이 그 글꼴로 보이게.
 * 미리보기 iframe 은 따로 싣는다(문서마다 `@font-face` 가 따로다).
 */
function installBrandFont(font: BrandLook['font']): void {
  const css = font ? brandFontFaceCss(font.url, font.mime) : '';
  const existing = document.getElementById(FONT_STYLE_ID);
  if (!css) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.textContent = css;
    return;
  }
  const style = document.createElement('style');
  style.id = FONT_STYLE_ID;
  style.textContent = css;
  document.head.append(style);
}

export function BrandProvider({ client, children }: { client: SignageOneClient; children: ReactNode }): ReactNode {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    client
      .settings()
      .then((value) => {
        if (!cancelled) setSettings(value);
      })
      // 못 읽어도 미리보기는 기본 모양으로 그린다 — 브랜드 때문에 콘솔을 막을 일은 아니다.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, nonce]);

  const brand = useMemo(() => brandFromSettings(settings), [settings]);
  useEffect(() => installBrandFont(brand.font), [brand.font]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const htmlOffline = settings?.html?.offline === true;
  const value = useMemo(() => ({ brand, htmlOffline, reload }), [brand, htmlOffline, reload]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
